import { cubic, join, length, resample, type Cut, type Pt } from "./ribbon";

/*
  The three room figures, all cut from one sheet. Each figure is a scene (its
  strips: tone, width, cut) and poses (one centreline per strip). A figure
  arrives by blending from its `flat` pose, the strips lying side by side as the
  uncut sheet, to its own pose, so every room visibly starts as the same paper.

  Coordinates are a 400 x 400 box; the SVG scales it.

  - String: two strips from opposite edges, joined where their ends overlap.
    Where the other one comes from is chance.
  - Knot: six strips woven over and under into one piece.
  - Loop: one strip joined end to end. When the room goes quiet the join opens
    and the strip lies loose; joined again, it is a ring.

  Outside the rooms, one more: Lost, a single vermilion strip cut free and
  lying loose on the sheet, for a link that leads nowhere (not found, and the
  route error on a public page).
*/

/**
 * A strip's fill. Paper, ink and vermilion are materials and never turn with the
 * hour; `fg` and `ground` are roles (what is read on bare paper, and the bare
 * paper itself), for strips that lie on the bare sheet and must turn with it at night.
 */
export type Tone = "paper" | "ink" | "vermilion" | "fg" | "ground";

export interface Strip {
  tone: Tone;
  width: number;
  cut: Cut;
}

/** A run of one strip's points drawn again on top of everything: where it crosses over. */
export interface Patch {
  strip: number;
  from: number;
  to: number;
}

export interface Scene {
  strips: Strip[];
  patches: Patch[];
}

/** One centreline per strip, each with the same point count as the scene's other poses. */
export type Pose = Pt[][];

/**
 * A figure at one instant of a scroll: its pose, and how much of each strip is
 * laid, as `[from, to]` fractions of the strip's length. A strip that is not
 * fully laid is still being fed in along its own line; it never stretches.
 */
export interface Frame {
  pose: Pose;
  laid?: [number, number][];
}

/** The figure at scroll progress `p`, 0 as it comes up the screen, 1 once it is read. */
export type Scrub = (p: number) => Frame;

/** 0 before `a`, 1 after `b`, and an S between: each strip starts and finishes gently. */
export const ramp = (p: number, a: number, b: number) => {
  const t = Math.max(0, Math.min(1, (p - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

const C = 200;

/** The uncut sheet: each strip laid flat as a band, stacked edge to edge around the centre. */
export function flat(scene: Scene, pose: Pose): Pose {
  const total = scene.strips.reduce((sum, s) => sum + s.width, 0);
  let y = C - total / 2;
  return pose.map((pts, i) => {
    const w = scene.strips[i].width;
    const len = Math.min(length(pts), 340);
    const band: Pt[] = [];
    for (let k = 0; k < pts.length; k++) band.push([C - len / 2 + (len * k) / (pts.length - 1), y + w / 2]);
    y += w;
    return band;
  });
}

/* ── String ─────────────────────────────────────────────────────────────── */

const STRING_POINTS = 40;
export const stringScene: Scene = {
  strips: [
    { tone: "paper", width: 30, cut: { start: 0.5, end: 0.6 } },
    { tone: "ink", width: 30, cut: { start: -0.5, end: 0.6 } },
  ],
  patches: [],
};

/** Where the two ends meet. */
const MEET: Pt = [214, 204];

/**
 * The String pose. Yours always comes in from the upper left; theirs comes in
 * from `angle` (radians, measured from the centre), bending one way or the
 * other by `bend` (-1..1). Their end runs past the meeting point so the two
 * ends overlap: attached.
 */
export function stringPose(angle = 0.6, bend = 1): Pose {
  const yours = resample(cubic([-40, 92], [96, 30], [150, 236], MEET, 60), STRING_POINTS);
  const start: Pt = [C + Math.cos(angle) * 300, C + Math.sin(angle) * 300];
  const dx = MEET[0] - start[0];
  const dy = MEET[1] - start[1];
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const end: Pt = [MEET[0] + (dx / len) * 26, MEET[1] + (dy / len) * 26];
  const theirs = resample(
    cubic(
      start,
      [start[0] + dx * 0.35 + nx * 90 * bend, start[1] + dy * 0.35 + ny * 90 * bend],
      [MEET[0] - dx * 0.25 - nx * 50 * bend, MEET[1] - dy * 0.25 - ny * 50 * bend],
      end,
      60
    ),
    STRING_POINTS
  );
  return [yours, theirs];
}

/** A fresh chance: an angle anywhere except the upper left, where yours comes from, and a bend. */
export function drawChance(random: () => number = Math.random): { angle: number; bend: number } {
  // From about 10 o'clock round to 7 o'clock, the long way, avoiding yours.
  const from = -0.9;
  const to = 2.5;
  return { angle: from + random() * (to - from), bend: random() < 0.5 ? -1 : 1 };
}

/**
 * The String on the landing, where the page's one thread is yours: it swings
 * into the figure and back out, and passes `THREAD_MEET` heading straight down.
 * Theirs is the only strip the figure draws. It comes in from chance, from
 * anywhere on the open side, and runs on over the thread and out past it: they collide.
 */
export const THREAD_MEET: Pt = [190, 210];

export const theirsScene: Scene = {
  strips: [{ tone: "ink", width: 30, cut: { start: -0.5, end: 0.6 } }],
  patches: [],
};

/** How far theirs runs on past the meeting point, across the thread and beyond: about 40% of it lies past the thread. */
export const THEIRS_PAST = 190;

/** Theirs, from `angle` (radians from the meeting point), bending by `bend`, its end run on past the thread. */
export function theirsPose(angle = Math.PI, bend = 1): Pose {
  const start: Pt = [THREAD_MEET[0] + Math.cos(angle) * 250, THREAD_MEET[1] + Math.sin(angle) * 250];
  const dx = THREAD_MEET[0] - start[0];
  const dy = THREAD_MEET[1] - start[1];
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  // On across the thread and out the other side: it does not just touch it, it hits it.
  const end: Pt = [THREAD_MEET[0] + (dx / len) * THEIRS_PAST, THREAD_MEET[1] + (dy / len) * THEIRS_PAST];
  return [
    resample(
      cubic(
        start,
        [start[0] + dx * 0.35 + nx * 70 * bend, start[1] + dy * 0.35 + ny * 70 * bend],
        [THREAD_MEET[0] - dx * 0.25 - nx * 40 * bend, THREAD_MEET[1] - dy * 0.25 - ny * 40 * bend],
        end,
        60
      ),
      STRING_POINTS
    ),
  ];
}

/** A fresh chance for theirs: from the open side, lower left round to upper left, never along the thread. */
export function threadChance(random: () => number = Math.random): { angle: number; bend: number } {
  const from = 0.62 * Math.PI;
  const to = 1.38 * Math.PI;
  return { angle: from + random() * (to - from), bend: random() < 0.5 ? -1 : 1 };
}

/** Theirs, scrolled: it reaches in once the thread has come past, and lands on it. */
export function theirsScrub(pose: Pose): Scrub {
  return (p) => ({ pose, laid: [[0, ramp(p, 0.62, 1)]] });
}

/* ── Knot ───────────────────────────────────────────────────────────────── */

const KNOT_POINTS = 31;
const KNOT_LINES = [130, 200, 270];
const KNOT_FROM = 40;
const KNOT_TO = 360;
const KNOT_WIDTH = 38;
const KNOT_TILT = (-7 * Math.PI) / 180;

const tilt = ([x, y]: Pt): Pt => {
  const cx = x - C;
  const cy = y - C;
  return [C + cx * Math.cos(KNOT_TILT) - cy * Math.sin(KNOT_TILT), C + cx * Math.sin(KNOT_TILT) + cy * Math.cos(KNOT_TILT)];
};

export const knotScene: Scene = {
  strips: [
    // Three across, paper; three down, ink. Drawn in this order, so down lies on top
    // until a patch puts an across strip back over it.
    ...KNOT_LINES.map((_, i) => ({ tone: "paper" as Tone, width: KNOT_WIDTH, cut: { start: i % 2 ? 0.4 : -0.4, end: 0.5 } })),
    ...KNOT_LINES.map((_, i) => ({ tone: "ink" as Tone, width: KNOT_WIDTH, cut: { start: i % 2 ? -0.4 : 0.4, end: -0.5 } })),
  ],
  // Over and under: an across strip passes over a down strip where (row + column) is even.
  patches: knotPatches(knotPose()),
};

/**
 * Where each over-crossing patch runs, read off the posed weave rather than the
 * rest grid, so the tilt and the hand-laid wobble cannot leave a sliver of the
 * down strip showing through.
 */
function knotPatches(pose: Pose): Patch[] {
  const rows = KNOT_LINES.length;
  const reach = KNOT_WIDTH / 2 + 10;
  const patches: Patch[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < rows; col++) {
      if ((row + col) % 2 !== 0) continue;
      const down = pose[rows + col];
      const near = pose[row].map((p) => Math.min(...down.map((q) => Math.hypot(p[0] - q[0], p[1] - q[1]))));
      const inside = near.flatMap((d, i) => (d <= reach ? [i] : []));
      patches.push({ strip: row, from: Math.max(0, inside[0] - 1), to: Math.min(KNOT_POINTS - 1, inside[inside.length - 1] + 1) });
    }
  }
  return patches;
}

export function knotPose(): Pose {
  const line = (fixed: number, across: boolean, seed: number): Pt[] => {
    const pts: Pt[] = [];
    for (let k = 0; k < KNOT_POINTS; k++) {
      const run = KNOT_FROM + ((KNOT_TO - KNOT_FROM) * k) / (KNOT_POINTS - 1);
      // A slight hand-laid wobble, so the weave reads as paper, not a grid.
      const wobble = 3.2 * Math.sin(run / 41 + seed * 1.7);
      pts.push(tilt(across ? [run, fixed + wobble] : [fixed + wobble, run]));
    }
    return pts;
  };
  return [...KNOT_LINES.map((y, i) => line(y, true, i)), ...KNOT_LINES.map((x, i) => line(x, false, i + 3))];
}

/** The order strips go in on the loom: across, down, across, down, so each new one weaves through the last. */
const KNOT_ORDER = [0, 3, 1, 4, 2, 5];

/** The Knot, scrolled: one string, then another tied through it, until six hold together. */
export function knotScrub(pose: Pose): Scrub {
  return (p) => ({
    pose,
    laid: pose.map((_, i) => {
      const k = KNOT_ORDER.indexOf(i);
      return [0, ramp(p, k * 0.1, k * 0.1 + 0.42)] as [number, number];
    }),
  });
}

/* ── Loop ───────────────────────────────────────────────────────────────── */

const LOOP_POINTS = 64;
const LOOP_RADIUS = 118;
/** How far the overlapping end rides out from the ring. */
const LOOP_LAP = 14;
/** Where the strip starts round the ring: about eleven o'clock. */
const LOOP_FROM = (-104 * Math.PI) / 180;

export const loopScene: Scene = {
  strips: [{ tone: "paper", width: 36, cut: { start: 0.55, end: 0.7 } }],
  patches: [],
};

/** Joined: a ring whose ends overlap by a few degrees, the way a paper loop is glued. */
export function loopJoined(): Pose {
  const pts: Pt[] = [];
  const sweep = (402 * Math.PI) / 180;
  for (let k = 0; k < LOOP_POINTS; k++) {
    const t = k / (LOOP_POINTS - 1);
    const a = LOOP_FROM + sweep * t;
    // The last stretch rides out over the first, so the glued overlap shows as a tab.
    const lap = Math.min(1, Math.max(0, (t - 0.86) / 0.14));
    const r = LOOP_RADIUS + LOOP_LAP * lap * lap * (3 - 2 * lap);
    pts.push([C + Math.cos(a) * r, C + Math.sin(a) * r]);
  }
  return [pts];
}

/** Archived: the join has come apart and the strip lies loose, still all there. */
export function loopOpen(): Pose {
  const loose = join(
    cubic([12, 292], [36, 118], [146, 86], [170, 214], 40),
    cubic([170, 214], [192, 336], [290, 350], [298, 232], 40),
    cubic([298, 232], [306, 112], [372, 96], [392, 176], 40)
  );
  return [resample(loose, LOOP_POINTS)];
}

/* ── Brane ──────────────────────────────────────────────────────────────── */

/*
  Not a room: the thing you share so that others can reach you. In string
  theory a brane is the surface open strings end on; here it is a disc, you,
  and strips come in to it from every side, each ending on it, one after
  another as the figure is read. Anyone can reach it from anywhere. The page's
  thread is one of them: it reaches the disc and goes on through it, under it
  (it is the same vermilion), and out the other side.

  It has its own section on the bare sheet, so the disc is vermilion, the
  Brane's and the String's colour, and the strips are the ground's ink (`fg`, a
  role, so by night they turn with the sheet).
*/

/** The disc, in the figure's 400 box: where it is and how big. */
export const BRANE_DISC = { cx: 210, cy: 200, r: 52 } as const;
const BRANE_STRIP = 24;
const BRANE_POINTS = 60;
const DISC_POINTS = 72;

/**
 * The strips that reach the disc: where each comes from (off the figure, so they
 * are fed in from outside) and the angle of the disc's rim it ends on, in the
 * figure's own coordinates (y down), so they arrive from all round it.
 */
const REACHERS: { from: Pt; angle: number }[] = [
  { from: [-70, 60], angle: -140 },
  { from: [-70, 380], angle: 140 },
  { from: [330, 400], angle: 62 },
  { from: [470, 120], angle: -28 },
];

function reachPose({ from, angle }: (typeof REACHERS)[number]): Pt[] {
  const { cx, cy, r } = BRANE_DISC;
  const a = (angle * Math.PI) / 180;
  const out: Pt = [Math.cos(a), Math.sin(a)];
  // Ends well on the disc, heading straight for its centre.
  const end: Pt = [cx + out[0] * (r - 12), cy + out[1] * (r - 12)];
  const lead: Pt = [end[0] + out[0] * 80, end[1] + out[1] * 80];
  return resample(cubic(from, [from[0] + (lead[0] - from[0]) * 0.5, from[1] + (lead[1] - from[1]) * 0.1], lead, end, 60), BRANE_POINTS);
}

/** The thread through the Brane, in the figure's box (before it is laid on the page). */
function threadPts(): Pt[] {
  const { cx, cy } = BRANE_DISC;
  return join(
    // In from the margin's side, over the top and down the left.
    cubic([400, 40], [320, 56], [100, 30], [100, 170], 70),
    // To the disc.
    cubic([100, 170], [100, 240], [150, cy + 16], [cx, cy], 50),
    // On through it, and out to the margin again.
    cubic([cx, cy], [cx + 60, cy - 16], [340, 250], [400, 262], 60)
  );
}

/**
 * The page's thread through the Brane, in the figure's box: in over the top and
 * down the left, to the disc, through it (the disc lies over it, and it is the
 * same colour, so it joins the disc and leaves it) and out to the right, the
 * margin's side. It never lies over the figure, so `over` is empty.
 */
export function braneThread(): { pts: Pt[]; over: [number, number][] } {
  return { pts: threadPts(), over: [] };
}

/** A disc as one strip: a ring of radius `r/2`, `r` wide, so its inside is filled right to the centre. */
function discPose(): Pt[] {
  const { cx, cy, r } = BRANE_DISC;
  const sweep = Math.PI * 2 + 0.16;
  return Array.from({ length: DISC_POINTS }, (_, i) => {
    const a = -Math.PI / 2 + (sweep * i) / (DISC_POINTS - 1);
    return [cx + (r / 2) * Math.cos(a), cy + (r / 2) * Math.sin(a)] as Pt;
  });
}

export const braneScene: Scene = {
  strips: [
    { tone: "vermilion", width: BRANE_DISC.r, cut: {} },
    // The strips that reach it, over it: each ends on it.
    ...REACHERS.map((_, i) => ({ tone: "fg" as Tone, width: BRANE_STRIP, cut: { start: i % 2 ? 0.5 : -0.5, end: i % 2 ? -0.4 : 0.4 } })),
  ],
  patches: [],
};

/** The disc stays put in the hand; the strips that reach it can be taken. */
export const BRANE_PINNED = [0];

export function branePose(): Pose {
  return [discPose(), ...REACHERS.map(reachPose)];
}

/** The Brane, scrolled: the disc is there from the first, and the strips reach it one after another, from every side. */
export function braneScrub(pose: Pose): Scrub {
  return (p) => ({
    pose,
    laid: pose.map((_, i) => (i === 0 ? ([0, 1] as [number, number]) : ([0, ramp(p, 0.15 + (i - 1) * 0.1, 0.5 + (i - 1) * 0.1)] as [number, number]))),
  });
}

/* ── Lost ───────────────────────────────────────────────────────────────── */

const LOST_POINTS = 48;

/** One vermilion strip, the colour of the thread: on bare paper it is the only colour. */
export const lostScene: Scene = {
  strips: [{ tone: "vermilion", width: 34, cut: { start: 0.6, end: -0.5 } }],
  patches: [],
};

/** Cut free and dropped: it lies across the sheet, curling once over itself, both ends loose. */
export function lostPose(): Pose {
  const loose = join(
    cubic([16, 318], [96, 352], [236, 330], [262, 246], 40),
    cubic([262, 246], [282, 180], [206, 150], [180, 196], 24),
    cubic([180, 196], [150, 248], [236, 286], [300, 214], 24),
    cubic([300, 214], [338, 170], [350, 116], [388, 92], 30)
  );
  return [resample(loose, LOST_POINTS)];
}
